/**
 * Zod shapes for `apps/api/src/readmodels/analytics-*.ts`, `money-out.ts`,
 * `recognised-vs-collected.ts` and `studio-summary.ts` — session 17 (A).
 *
 * `.docs/00-index.md` §5: "Cross-module read models for a page —
 * `apps/api/src/readmodels/<name>.ts`… `Shape` is a zod schema in
 * `packages/shared/src/schemas/readmodels/<name>.ts`." Every field carries
 * `.describe()`, all the way down, because these shapes double as the `output` of
 * the ten `analytics_*` actions in `apps/api/src/modules/core/analytics-actions.ts`
 * — `defineAction` refuses an undescribed field at import, at any depth.
 *
 * Money that is a mix of currencies before it reaches a read model — a retainer's
 * `monthly_amount`, an invoice's `total` — is converted to its USD-equivalent by
 * the read model before it is shaped into one of these, exactly once, via
 * `catalog.fx` (`.docs/17-studio-read-models.md` §Edge cases: "EUR clients in
 * every USD aggregate: converted at `fx`… the native amount is always returned
 * beside the USD-eq"). A field whose description does not say "USD-equivalent" is
 * already in the currency it names.
 */
import { z } from 'zod';
import { DateOnlySchema, MoneySchema, PeriodSchema, Rfc3339Schema } from '../primitives.js';
import { ExpenseKindSchema, ExpenseSchema } from '../expenses.js';
/* -------------------------------------------------------------------- MRR */
export const AnalyticsMrrPointSchema = z
    .object({
    month: PeriodSchema.describe('The month this reading is for.'),
    mrr: MoneySchema.describe('MRR at the end of this month, USD-equivalent — every live retainer’s ' +
        'monthly_amount as it stood on the last day, converted and rounded to $10.'),
})
    .describe('One point of the recurring-revenue series — `formulae.md` §MRR at month end.');
export const AnalyticsMrrMovementSchema = z
    .object({
    month: PeriodSchema.describe('The month this movement happened in.'),
    new: MoneySchema.describe('MRR from retainers whose started_on falls in this month.'),
    expansion: MoneySchema.describe('MRR added by a package change applied this month, positive.'),
    contraction: MoneySchema.describe('MRR removed by a package change applied this month, as a negative amount.'),
    churn: MoneySchema.describe('The whole of a cancelled retainer’s last-known MRR, as a negative amount, in the ' +
        'month the cancellation took effect.'),
    net: MoneySchema.describe('new + expansion + contraction + churn, signed.'),
})
    .describe('One month’s new, expansion, contraction and churn — `formulae.md` §MRR movements.');
export const AnalyticsMrrSchema = z
    .object({
    as_of: PeriodSchema.describe('The latest month in both series below — the current month.'),
    series: z
        .array(AnalyticsMrrPointSchema)
        .describe('MRR at the end of every month from January of this year to as_of.'),
    movements: z
        .array(AnalyticsMrrMovementSchema)
        .describe('The same months as `series`, broken into new, expansion, contraction and churn.'),
})
    .describe('The recurring-revenue series and its month-by-month movements.');
/* ------------------------------------------------------- revenue by model */
export const RECOGNISED_MODELS = ['retainer', 'project', 'extra', 'usage'];
export const RecognisedModelSchema = z
    .enum(RECOGNISED_MODELS)
    .describe('Which billing model a recognised line belongs to — retainer, project (deposit, ' +
    'milestone, final, scope_change), extra, or usage.');
export const AnalyticsRevenueMonthSchema = z
    .object({
    month: PeriodSchema.describe('The month these invoices were issued in.'),
    retainer: MoneySchema.describe('Recognised retainer revenue this month.'),
    project: MoneySchema.describe('Recognised project revenue this month.'),
    extra: MoneySchema.describe('Recognised extra revenue this month.'),
    usage: MoneySchema.describe('Recognised usage revenue this month.'),
    total: MoneySchema.describe('The sum of the four models.'),
})
    .describe('One month’s recognised revenue, split by model.');
export const RevenueShareSchema = z
    .object({
    model: RecognisedModelSchema,
    amount: MoneySchema.describe('Recognised revenue of this model, in the current month.'),
    share: z.number().describe('This model’s fraction of the current month’s total, 0 to 1.'),
})
    .describe('One model’s share of the current month’s recognised revenue.');
export const AnalyticsRevenueByModelSchema = z
    .object({
    months: z
        .array(AnalyticsRevenueMonthSchema)
        .describe('Recognised revenue by model, oldest month first.'),
    current: z
        .object({
        total: MoneySchema.describe('The current month’s recognised revenue, all models.'),
        shares: z.array(RevenueShareSchema).describe('Each model’s share of that total.'),
    })
        .describe('The current month, broken into shares — `client/analytics.html`’s bar.'),
})
    .describe('Recognised revenue by billing model — `formulae.md` §Recognised revenue.');
/* ---------------------------------------------------------- effective rate */
export const ClientEffectiveRateSchema = z
    .object({
    client: z.string().describe('The client’s slug.'),
    name: z
        .string()
        .describe('The company as everybody says it — "Northbay Dental", not the slug.'),
    paid: MoneySchema.describe('What this month bills — the retainer line for this period plus the extras agreed in ' +
        'it, USD-equivalent to the cent (`formulae.md` §Paid). The same figure ' +
        '`analytics_client_health.paid` and `clients_summary` divide, so the three cannot ' +
        'disagree. Not the extras an invoice recognised this month: the run bills extras in ' +
        'arrears, so that invoice carries last month’s work.'),
    minutes: z
        .number()
        .int()
        .describe('Retainer-billable minutes logged this month (billable_kind = retainer).'),
    rate: MoneySchema.nullable().describe('paid ÷ hours, in USD, or null when there are no retainer time entries — never ' +
        'Infinity and never zero.'),
    under_floor: z
        .boolean()
        .describe('True when rate is below the floor. Always false when rate is null.'),
})
    .describe('One client’s effective $/hour for the month.');
export const AnalyticsEffectiveRateSchema = z
    .object({
    period: PeriodSchema.describe('The month this reading is for.'),
    floor: MoneySchema.describe('setting(billing.rate_floor) — the line a rate is judged against.'),
    clients: z
        .array(ClientEffectiveRateSchema)
        .describe('Every client with a live retainer or retainer time this month.'),
    portfolio: MoneySchema.nullable().describe('Every client’s paid summed, divided by every client’s minutes summed — not an ' +
        'average of averages. Null when nobody logged retainer time this month.'),
})
    .describe('Effective $/hour, per client and for the portfolio — `formulae.md` §Effective $/hour.');
/* -------------------------------------------------------------- client health */
export const ClientScopeStatusSchema = z
    .object({
    client: z.string().describe('The client’s slug.'),
    name: z
        .string()
        .describe('The company as everybody says it — "Northbay Dental", not the slug.'),
    period: PeriodSchema.describe('The month this reading is for.'),
    value: MoneySchema.describe('Value delivered this month, at current rate-card prices, USD (`formulae.md` §Value ' +
        'delivered).'),
    paid: MoneySchema.describe('What this month bills — the retainer line for this period plus the extras agreed in ' +
        'it, USD (`formulae.md` §Paid). The same figure `analytics_effective_rate.paid` ' +
        'divides by hours.'),
    ratio: z
        .number()
        .nullable()
        .describe('value ÷ paid. Null when paid is zero — nothing was billed to compare against.'),
    months_over: z
        .number()
        .int()
        .describe('How many months running, ending with this one, the ratio has been over 130% — reset ' +
        'to zero the first month it is not.'),
    under_scoped: z
        .boolean()
        .describe('True at two or more consecutive months over 130%. One hot month is a watch.'),
    watch: z
        .boolean()
        .describe('True this month is over 130% and under_scoped is not — the one-month case.'),
    action: z
        .string()
        .nullable()
        .describe('The client’s account_note, staff-written — what to do about it, or null.'),
})
    .describe('One client’s scope health for the month — `formulae.md` §% of value, under-scoped.');
export const AnalyticsUnderScopedSchema = z
    .object({
    period: PeriodSchema.describe('The month this reading is for.'),
    clients: z
        .array(ClientScopeStatusSchema)
        .describe('Every client with a live retainer this month, worst ratio first.'),
})
    .describe('Client health: value delivered against what was paid, and who is under-scoped.');
/* -------------------------------------------------------------------- tier mix */
export const MARKET_TIERS = [1, 2, 3];
export const TierMixRowSchema = z
    .object({
    tier: z.union([z.literal(1), z.literal(2), z.literal(3)]).describe('The market tier, 1 to 3.'),
    mrr: MoneySchema.describe('This tier’s share of MRR, USD-equivalent.'),
    share: z.number().describe('This tier’s fraction of total MRR, 0 to 1.'),
})
    .describe('One market tier’s share of MRR.');
export const AnalyticsTierMixSchema = z
    .object({
    total: MoneySchema.describe('MRR across every tier, USD-equivalent.'),
    tiers: z.array(TierMixRowSchema).describe('Tier 1 to Tier 3, in that order.'),
})
    .describe('MRR by market tier — `studio/analytics.html` §Mix and pipeline.');
/* ----------------------------------------------------------------- package mix */
export const PACKAGE_SHAPES = ['custom', 'preset'];
export const PackageShapeSchema = z
    .enum(PACKAGE_SHAPES)
    .describe('custom — built for one client; preset — a named, reusable package.');
export const PackageMixRowSchema = z
    .object({
    shape: PackageShapeSchema,
    clients: z
        .array(z.object({
        client: z.string().describe('The client’s slug.'),
        name: z.string().describe('The company as everybody says it.'),
    }))
        .describe('The clients on this shape — the slug to link by, and the name to print.'),
    mrr: MoneySchema.describe('This shape’s share of MRR, USD-equivalent.'),
    blocks: z.number().int().describe('Total retainer_item rows across these clients.'),
})
    .describe('One package shape’s clients, MRR and block count.');
export const AnalyticsPackageMixSchema = z
    .object({
    rows: z.array(PackageMixRowSchema).describe('Custom, then preset.'),
    total: z
        .object({
        clients: z.number().int().describe('Every client with a live retainer.'),
        mrr: MoneySchema.describe('MRR across every shape, USD-equivalent.'),
        blocks: z.number().int().describe('Every retainer_item row across every client.'),
    })
        .describe('The row every other row sums to.'),
    average_blocks_per_client: z
        .number()
        .describe('total.blocks ÷ total.clients, to one decimal — never recomputed by a caller.'),
    clients_at_bundle_band: z
        .number()
        .int()
        .describe('Clients whose block count meets the catalog’s lowest bundle-discount band.'),
    clients_at_minimum: z.number().int().describe('Clients with exactly one block.'),
    most_common_block: z
        .object({
        service: z.string().describe('The rate-card service code.'),
        clients: z.number().int().describe('How many clients have a block of it.'),
    })
        .nullable()
        .describe('The service the most clients have a block of, or null with no retainers.'),
})
    .describe('The package shapes on the book — `studio/analytics.html` §Package mix.');
/* ------------------------------------------------------------------- pipeline */
export const PIPELINE_KINDS = ['project', 'retainer_change'];
export const PipelineKindSchema = z
    .enum(PIPELINE_KINDS)
    .describe('project — a proposed work order; retainer_change — a proposed package change.');
export const PipelineItemSchema = z
    .object({
    kind: PipelineKindSchema,
    ref: z.string().describe('WO-2026-014 for a project, RET-CHG-004 for a package change.'),
    client: z.string().describe('The client’s slug.'),
    name: z
        .string()
        .describe('The company as everybody says it — "Northbay Dental", not the slug.'),
    /**
     * What the work is, **without the client in it**.
     *
     * `mockups/studio/analytics.html` prints "Northbay Dental — Website M": the client
     * and then the work, composed by the page out of `name` and this. A package change
     * has no title of its own and this used to be ``Package change — ${slug}``, so the
     * page printed the client twice and once as a slug.
     */
    title: z.string().describe('What it is, as a client would read it. No client name in it.'),
    amount: MoneySchema.nullable().describe('What it would be worth — a project’s total, or a package change’s delta. Null for a ' +
        'quote-only project nobody has priced yet.'),
    proposed_at: Rfc3339Schema.nullable().describe('When it was sent to the client, or null for a draft.'),
})
    .describe('One thing quoted or proposed and not yet decided.');
export const AnalyticsPipelineSchema = z
    .object({
    total: MoneySchema.describe('Every proposed project’s own total summed, USD-equivalent — `analytics.html`’s ' +
        '"$6,200 quoted". A one-time total and a package change’s $/mo delta are different ' +
        'units and are never summed into one figure.'),
    monthly_delta: MoneySchema.describe('Every proposed package change’s delta summed, USD-equivalent, signed — a positive ' +
        'figure is net expansion in the pipeline, a negative one net contraction.'),
    items: z.array(PipelineItemSchema).describe('Newest proposal first.'),
})
    .describe('Proposed work orders and proposed package changes — `formulae.md` has no pipeline ' +
    'section; this reads what session 11 and 12 already track as proposed and undecided.');
/* --------------------------------------------------------------- receivables */
export const PastDueBandSchema = z
    .object({
    band: z.string().describe('One of PAST_DUE_BUCKETS’ labels — "1–30 d", "31–60 d", …'),
    amount: MoneySchema.describe('Outstanding remainder in this band, USD-equivalent.'),
    count: z.number().int().describe('How many invoices are in this band.'),
})
    .describe('One ageing band of past-due money.');
export const PastDueInvoiceSchema = z
    .object({
    ref: z.string().describe('The invoice’s reference.'),
    client: z.string().describe('The client’s slug.'),
    outstanding: MoneySchema.describe('What is still owed, in the invoice’s own currency.'),
    due_on: DateOnlySchema.describe('When it was due.'),
    days_past_due: z.number().int().describe('Whole days since due_on.'),
})
    .describe('One overdue invoice, in its own currency.');
export const AnalyticsReceivablesSchema = z
    .object({
    as_of: DateOnlySchema.describe('The day ages were measured against.'),
    total: MoneySchema.describe('Every band summed, USD-equivalent.'),
    bands: z.array(PastDueBandSchema).describe('Non-empty bands only, youngest first.'),
    invoices: z
        .array(PastDueInvoiceSchema)
        .describe('Every past-due invoice, oldest due_on first.'),
})
    .describe('What is owed and how old it is — `formulae.md` §Past due.');
/* ---------------------------------------------------------------- money out */
export const ExpenseKindTotalSchema = z
    .object({
    kind: ExpenseKindSchema,
    total: MoneySchema.describe('Every expense of this kind this month, summed.'),
})
    .describe('One expense kind’s share of the month.');
export const MoneyOutSchema = z
    .object({
    period: PeriodSchema.describe('The month this ledger is for.'),
    lines: z.array(ExpenseSchema).describe('Every expense row this month, oldest paid_on first.'),
    subscriptions: z
        .object({
        rows: z.array(ExpenseSchema).describe('The kind = subscription rows, folded on the page.'),
        total: MoneySchema.describe('Their sum — the one line the main table shows.'),
    })
        .describe('`studio/money.html`’s "the eight subscriptions behind that first line".'),
    by_kind: z.array(ExpenseKindTotalSchema).describe('Every kind present this month, summed.'),
    total: MoneySchema.describe('Money out, this month — every line summed.'),
    draw: MoneySchema.describe('The kind = draw lines, summed.'),
    delivery_and_running: MoneySchema.describe('total − draw.'),
    collected: MoneySchema.describe('Succeeded charge payments received this month, gross.'),
    net_cash: MoneySchema.describe('collected − total.'),
    opening_balance: MoneySchema.nullable().describe('cash_position for this period, or null when nobody has set one.'),
    closing_balance: MoneySchema.nullable().describe('opening_balance + net_cash, or null when there is no opening balance to add it to.'),
})
    .describe('Money out for one month, and where that leaves the account — `studio/money.html`.');
/* ------------------------------------------------------- recognised vs collected */
export const RecognisedVsCollectedMonthSchema = z
    .object({
    month: PeriodSchema.describe('The month.'),
    recognised: MoneySchema.describe('Invoice totals issued this month, USD-equivalent.'),
    collected: MoneySchema.describe('Succeeded charge payments received this month, USD-equivalent.'),
})
    .describe('One month’s recognised revenue beside what was actually collected.');
export const RecognisedVsCollectedSchema = z
    .object({
    months: z
        .array(RecognisedVsCollectedMonthSchema)
        .describe('The trailing months, oldest first — six on `studio/money.html`.'),
})
    .describe('Recognised revenue and collected cash, side by side, over several months.');
/* ------------------------------------------------------------------- summary */
export const StudioSummarySchema = z
    .object({
    clients: z
        .number()
        .int()
        .describe('Clients with a live retainer — active or past_due. Paused ones are served, not billed.'),
    mrr: MoneySchema.describe('MRR at the end of the current month, USD-equivalent.'),
    line: z.string().describe('"N clients · MRR $X" — the sidebar’s own sentence.'),
})
    .describe('The one-line studio summary — `formulae.md` §Studio summary line.');
//# sourceMappingURL=analytics.js.map